/**
 * 文本输入节点 - 用于输入 AI 提示词
 */
import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { useReactFlow, useStore } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { toast } from 'react-toastify';
import { NodeLayout } from './NodeLayout';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ModelSelectionModal from '../../ModelSelectionModal';
import { getModelsByMode, getDefaultModelData } from './modelConfigs';
import './PromptInputNode.css';

// 根据目标节点类型返回占位符和提示文本
const getPlaceholderByTargetType = (targetType, targetHandle) => {
  const config = {
    // 图像生成类
    gptImage2: { placeholder: '描述画面、构图和需要呈现的文字...', hint: 'GPT Image 2 适合高精度画面与文字生成' },
    nanoBanana2: { placeholder: '描述你想快速生成或编辑的画面...', hint: 'Nano Banana 2 支持最多 14 张图片参考' },
    textToImage: { placeholder: '请输入图像描述...', hint: '描述你想要生成的图像内容' },
    referenceToImage: { placeholder: '请输入图像描述...', hint: '描述你想要生成的图像内容' },
    imengImageGen: { placeholder: '请输入图像描述...', hint: '描述你想要生成的图像内容' },
    minimaxImage: { placeholder: '请输入图像描述...', hint: '描述你想要生成的图像内容' },
    googleBanana: { placeholder: '请输入图像描述...', hint: '描述你想要生成的图像内容' },
    bananaPro: { placeholder: '请输入图像描述...', hint: '描述你想要生成的图像内容，支持精准文字' },
    
    // 视频生成类
    minimaxH3Video: { placeholder: '描述镜头、动作、节奏和参考素材关系...', hint: 'MiniMax-H3 支持最多 9 张参考图和 3 个参考视频' },
    klingO3ReferenceVideo: { placeholder: '使用 @Image1 引用图片、@Element1 引用视频元素...', hint: 'Kling O3 Standard 支持图片和视频参考，合计最多 4 个' },
    geminiOmniFlashVideo: { placeholder: '描述画面动作和参考图主体...', hint: 'Gemini Omni Flash 支持 1-5 张参考图' },
    superSeed2LiteVideo: { placeholder: '使用【@图片1】、【@视频1】、【@音频1】引用参考素材...', hint: 'Seedance 2.5 支持图片、视频和音频全能参考' },
    sora2Video: { placeholder: '请输入视频描述...', hint: '描述你想要生成的视频内容' },
    veo3Video: { placeholder: '请输入视频描述...', hint: '描述你想要生成的视频内容' },
    doubaoVideo: { placeholder: '请输入视频描述...', hint: '描述你想要生成的视频内容' },
    hailuo23Video: { placeholder: '请输入视频描述...', hint: '描述你想要生成的视频内容' },
    klingVideo: { placeholder: '请输入视频描述...', hint: '描述你想要生成的视频内容' },
    klingLipSync: { placeholder: '请输入语音文本...', hint: '输入需要对口型的语音内容' },
    
    // 音频生成类
    textToAudio: { placeholder: '请输入要朗读的文本...', hint: '输入需要转换为语音的文本内容' },
    hailuoMusic: { 
      prompt: { placeholder: '请输入音乐描述...', hint: '描述音乐风格、情绪、乐器等特征' },
      lyrics: { placeholder: '请输入歌词...', hint: '输入歌词内容，可使用 [Verse]、[Chorus] 等标签' },
    },
    sunoMusic: { placeholder: '请输入歌词...', hint: '输入歌词内容，可使用 [Verse]、[Chorus] 等标签' },
  };

  // 处理 hailuoMusic 的双 Handle 情况
  if (targetType === 'hailuoMusic' && config[targetType][targetHandle]) {
    return config[targetType][targetHandle];
  }

  return config[targetType] || { placeholder: '请输入描述文本...', hint: '输入你想要生成的内容描述' };
};

export const PromptInputNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getNodes } = useReactFlow();
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes);
  const { readOnly } = useProject();
  const { addNodesWithEdges } = useNodeOperations();
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [textareaHeight, setTextareaHeight] = useState(data.textareaHeight || 150);

  // 根据连接的目标节点动态获取占位符和提示文本
  const { placeholder, hint } = useMemo(() => {
    // 查找从当前节点输出的边
    const outgoingEdge = edges.find(edge => edge.source === id);
    
    if (outgoingEdge) {
      const targetNode = nodes.find(node => node.id === outgoingEdge.target);
      if (targetNode) {
        return getPlaceholderByTargetType(targetNode.type, outgoingEdge.targetHandle);
      }
    }
    
    // 默认值
    return { placeholder: '请输入描述文本...', hint: '输入你想要生成的内容描述' };
  }, [edges, nodes, id]);

  // 弹窗状态
  const [showModelModal, setShowModelModal] = useState(false);
  const [modalMode, setModalMode] = useState('image'); // 'image' | 'video' | 'audio'

  // 更新节点数据
  useEffect(() => {
    if (!readOnly) {
      updateNodeData(id, { prompt, textareaHeight });
    }
  }, [id, prompt, textareaHeight, updateNodeData, readOnly]);

  // 处理 textarea 的 resize
  const handleTextareaMouseDown = useCallback((e) => {
    // 检测是否点击在 resize 区域（右下角 16x16 像素）
    const rect = e.target.getBoundingClientRect();
    const isResizeArea = 
      e.clientX > rect.right - 16 && 
      e.clientY > rect.bottom - 16;
    
    if (isResizeArea) {
      e.stopPropagation();
      
      const startY = e.clientY;
      const startHeight = e.target.offsetHeight;
      
      const handleMouseMove = (moveEvent) => {
        const newHeight = Math.max(80, startHeight + moveEvent.clientY - startY);
        setTextareaHeight(newHeight);
      };
      
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  }, []);

  // 阻止拖拽以支持文字选择
  const handleTextareaInteraction = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // 计算新节点位置，避免重叠
  const calculateModelNodePosition = useCallback((sourceNode, existingNodes) => {
    const baseOffset = { x: 600, y: 0 }; // 增加水平偏移量到 600px
    let position = {
      x: sourceNode.position.x + baseOffset.x,
      y: sourceNode.position.y + baseOffset.y,
    };

    // 检测重叠，如果有重叠则向下偏移
    const nodeWidth = 320;
    const nodeHeight = 200;
    const verticalOffset = 150;

    let hasOverlap = true;
    let attempts = 0;
    const maxAttempts = 10;

    while (hasOverlap && attempts < maxAttempts) {
      hasOverlap = existingNodes.some((node) => {
        if (!node.position) return false;

        const dx = Math.abs(node.position.x - position.x);
        const dy = Math.abs(node.position.y - position.y);

        return dx < nodeWidth && dy < nodeHeight;
      });

      if (hasOverlap) {
        position.y += verticalOffset;
        attempts++;
      }
    }

    return position;
  }, []);

  // 处理模型选择
  const handleModelSelect = useCallback(async (modelType) => {
    try {
      // 关闭弹窗
      setShowModelModal(false);

      // 获取当前节点信息
      const sourceNode = getNode(id);
      if (!sourceNode) {
        toast.error('获取源节点失败');
        return;
      }

      // 获取所有现有节点
      const existingNodes = getNodes();

      // 计算新节点位置
      const position = calculateModelNodePosition(sourceNode, existingNodes);

      // 创建新的模型节点
      const newNodeId = nanoid();
      const newNode = {
        id: newNodeId,
        type: modelType,
        position,
        data: getDefaultModelData(modelType),
        selected: true, // 选中新创建的节点
      };

      // 创建连接边
      const newEdge = {
        id: nanoid(),
        source: id,
        sourceHandle: 'output',
        target: newNodeId,
        targetHandle: 'prompt',
        type: 'default',
      };

      // 添加节点和边
      await addNodesWithEdges([newNode], [newEdge]);

      // 显示成功提示
      const modelNames = {
        gptImage2: 'GPT 2 图像生成',
        nanoBanana2: 'Nano 2 图像生成',
        minimaxH3Video: '海螺 H3 视频生成',
        klingO3ReferenceVideo: '可灵 O3 视频生成',
        geminiOmniFlashVideo: 'Gemini 极速视频生成',
        superSeed2LiteVideo: 'Seedance 2.5 视频生成',
        imengImageGen: '即梦4.0生图',
        minimaxImage: '海螺画图',
        googleBanana: '谷歌nano banana',
        textToImage: '文生图',
        referenceToImage: '参考生图',
        sora2Video: 'SORA2视频',
        veo3Video: 'VEO3视频',
        klingVideo: '可灵2.5',
        hailuo23Video: '海螺2.3视频',
        doubaoVideo: '豆包视频',
        textToAudio: '语音合成',
        hailuoMusic: '音乐生成',
      };

      toast.success(`已创建 ${modelNames[modelType] || modelType} 节点`);
    } catch (error) {
      console.error('创建模型节点失败:', error);
      toast.error('创建节点失败，请重试');
    }
  }, [id, getNode, getNodes, calculateModelNodePosition, addNodesWithEdges]);

  // 图片按钮点击
  const handleImageButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    setModalMode('image');
    setShowModelModal(true);
  }, [readOnly]);

  // 视频按钮点击
  const handleVideoButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    setModalMode('video');
    setShowModelModal(true);
  }, [readOnly]);

  // 音频按钮点击
  const handleAudioButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    setModalMode('audio');
    setShowModelModal(true);
  }, [readOnly]);

  return (
    <>
      <NodeLayout
        id={id}
        type="promptInput"
        title="文本输入"
        showInputHandle={false}
        showOutputHandle={true}
      >
        <div className="PromptInputNode-wrapper">
          {/* 右侧悬浮超级按钮组 */}
          <div className="PromptInputNode-super-buttons">
            {/* 图片按钮 */}
            <button
              className="PromptInputNode-super-button PromptInputNode-super-button-image"
              title="添加图片"
              disabled={readOnly}
              onClick={handleImageButtonClick}
            >
              <i className="bi bi-image-fill"></i>
              <span className="PromptInputNode-super-button-label">图片</span>
            </button>

            {/* 视频按钮 */}
            <button
              className="PromptInputNode-super-button PromptInputNode-super-button-video"
              title="添加视频"
              disabled={readOnly}
              onClick={handleVideoButtonClick}
            >
              <i className="bi bi-camera-video-fill"></i>
              <span className="PromptInputNode-super-button-label">视频</span>
            </button>

            {/* 音频按钮 */}
            <button
              className="PromptInputNode-super-button PromptInputNode-super-button-audio"
              title="添加音频"
              disabled={readOnly}
              onClick={handleAudioButtonClick}
            >
              <i className="bi bi-music-note-beamed"></i>
              <span className="PromptInputNode-super-button-label">音频</span>
            </button>
          </div>

          <div className="PromptInputNode-form">
            <label className="PromptInputNode-label">
              <svg className="PromptInputNode-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 2h10M2 5h10M2 8h10M2 11h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              描述文本
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={placeholder}
              className="PromptInputNode-textarea nodrag nowheel"
              style={{ height: textareaHeight }}
              disabled={readOnly}
              readOnly={readOnly}
              onMouseDown={handleTextareaMouseDown}
              onPointerDown={handleTextareaInteraction}
            />
          </div>
          <div className="PromptInputNode-hint">
            {hint}
          </div>
        </div>
      </NodeLayout>

      {/* 模型选择弹窗 */}
      <ModelSelectionModal
        show={showModelModal}
        onHide={() => setShowModelModal(false)}
        mode={modalMode}
        models={getModelsByMode(modalMode)}
        onSelect={handleModelSelect}
      />
    </>
  );
});

PromptInputNode.displayName = 'PromptInputNode';
